// The app's singletons. Everything testable takes these as arguments; only this file touches the real window.
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { isStopRefusal } from "../features/billing/view";
import { ApiError, createApiClient, shouldRetry } from "./api";
import { queryKeys } from "./query-keys";
import { createHostBridge, type BridgeWindow } from "./host-bridge";
import { createSessionStore } from "./session";
import { browserPushEnv, deviceTokenFor, forgetDeviceToken } from "./web-push";

export const bridge = createHostBridge(window as unknown as BridgeWindow);

/** The app's own address, the only one that stays signed in through the cookie. Production is the default; e2e sets VITE_APP_URL. */
const appOrigin = (): string | undefined => {
  try {
    return new URL((import.meta.env.VITE_APP_URL as string | undefined) || "https://app.imagora.media").origin;
  } catch {
    return undefined;
  }
};

export const api = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? "",
  getToken: () => sessionStore.getToken(),
  refreshSession: () => sessionStore.refreshAfterUnauthorized(),
  pageOrigin: window.location.origin,
  appOrigin: appOrigin(),
});

/** The public site with the legal pages. Production sets VITE_SITE_URL; the default is the one the site itself uses. */
export const siteUrl = (): string => ((import.meta.env.VITE_SITE_URL as string | undefined) || "https://imagora.media").replace(/\/+$/, "");

/** Where the API answers, as a full address someone can paste into another tool. */
export const apiBaseUrl = (): string => (import.meta.env.VITE_API_URL as string | undefined) || window.location.origin;

/** Browser storage can throw, in a private window or with site data blocked. Then a reload asks for a new link. */
function tabStorage(key: string) {
  return {
    load(): string | null {
      try {
        return window.sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    save(value: string | null) {
      try {
        if (value) window.sessionStorage.setItem(key, value);
        else window.sessionStorage.removeItem(key);
      } catch {
        // Stays signed in until the next reload.
      }
    },
  };
}

/** Only whether this browser was signed in to stay so: the session itself is in a cookie the page cannot read. */
function stayingHint(key: string) {
  return {
    hinted(): boolean {
      try {
        return window.localStorage.getItem(key) === "1";
      } catch {
        return false;
      }
    },
    hint(on: boolean) {
      try {
        if (on) window.localStorage.setItem(key, "1");
        else window.localStorage.removeItem(key);
      } catch {
        // Then the next reload asks for a code again.
      }
    },
  };
}

/** The browser's push, for the notifications screen and for a tapped notice. */
export const pushEnv = browserPushEnv(bridge.embedded);

export const sessionStore = createSessionStore({
  exchange: (token) => api.exchange(token),
  bridge,
  direct: { ...tabStorage("imagora.direct-session"), restore: (token) => api.account.restore(token) },
  staying: { ...stayingHint("imagora.stays-signed-in"), tabTenant: tabStorage("imagora.tab-workspace"), refresh: (input) => api.account.refresh(input) },
  reopen: (token, tenantId) => api.account.switchTenantWith(token, tenantId),
  device: {
    load: (tenantId) => deviceTokenFor(pushEnv, tenantId),
    forget: (token) => forgetDeviceToken(pushEnv, token),
    open: (token) => api.account.deviceSession(token),
  },
});

/** The platform role's token, kept apart from any tenant session so neither can be sent by mistake in the other's place. */
export const platformToken = tabStorage("imagora.platform-session");

/** The operator's client: the same calls and errors, its own token, no refresh. */
export const platformApi = createApiClient({
  baseUrl: import.meta.env.VITE_API_URL ?? "",
  getToken: () => platformToken.load(),
  refreshSession: () => Promise.resolve(false),
});

/** A workspace stopped while the app was open: the first 402 of the stop reads the status again, and the shell draws the payment screen. */
function stopNoticed(error: unknown) {
  if (isStopRefusal(error)) void queryClient.invalidateQueries({ queryKey: queryKeys.billingStatus, exact: true });
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: stopNoticed }),
  // AI work refused for want of a working key: the banner that says so, and how to fix it, appears at once.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (error instanceof ApiError && error.code.startsWith("ai_key")) void queryClient.invalidateQueries({ queryKey: queryKeys.aiKeys });
      stopNoticed(error);
    },
  }),
  defaultOptions: {
    queries: { retry: shouldRetry, staleTime: 30_000, refetchOnWindowFocus: false },
    mutations: { retry: false },
  },
});
